import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QrCode } from "lucide-react";
import "@/App.css";
import { OrderProvider, hasLiveTableSession, useOrder } from "@/store/orderStore";
import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { AuthSheetProvider } from "@/auth/AuthSheetProvider";
import { TenantProvider, useTenant } from "@/tenant/TenantProvider";
import { useQrTable } from "@/tenant/useQrTable";
import { fetchJson } from "@/lib/api";
import { EVENTS, track } from "@/lib/analytics";
import { lastQuizKey, pendingOrderKey } from "@/lib/storageKeys";
import { LANGUAGE, ROUTE } from "@/constants/testIds";
import { LanguageMenu } from "@/components/LanguageSwitcher";
import { FuellyBadge } from "@/components/FuellyBrand";
import { ToastProvider, useToast } from "@/components/Toasts";
import GuestEntry from "@/screens/GuestEntry";
import ModeSelect from "@/screens/ModeSelect";
import BuildPlate from "@/screens/BuildPlate";
import Drinks from "@/screens/Drinks";
import Menu from "@/screens/Menu";
import Cart from "@/screens/Cart";
import Quiz from "@/screens/Quiz";
import Q5Input from "@/screens/Q5Input";
import AiPlateResult from "@/screens/AiPlateResult";
import Confirmation from "@/screens/Confirmation";
import PaymentSuccess from "@/screens/PaymentSuccess";
import Profile from "@/screens/Profile";
import Landing from "@/screens/Landing";
import Legal from "@/screens/Legal";
import ScanRequired from "@/screens/ScanRequired";


// Every screen a URL segment may name. The flow stays a state machine — the URL
// is where the current state is written, so each step is a history entry (the
// phone's Back walks the flow instead of leaving the site) and a page view a
// funnel can count. Anything else in the path bounces to the base.
const STEPS = new Set([
  "guest",
  "home",
  "build",
  "drinks",
  "menu",
  "quiz",
  "q5",
  "ai",
  "cart",
  "confirm",
  "paid",
  "profile",
]);

const Shell = () => {
  const { state, addPlateToCart, addReorderToCart, placeOrder, resetAll } = useOrder();
  const { slug, findDish, restaurant } = useTenant();
  const { user, refreshUser } = useAuth();
  const { qrError, dismissQrError, scanning } = useQrTable(slug);
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const splat = (useParams()["*"] || "").replace(/\/+$/, "");
  const screen = splat === "" ? "guest" : splat;
  const [quizAnswers, setQuizAnswers] = useState(null);
  const [freeText, setFreeText] = useState("");
  // What this device answered last time here, offered back on the first step.
  const [lastQuiz, setLastQuiz] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(lastQuizKey(slug)) || "null");
    } catch (e) {
      return null;
    }
  });
  // Whether this render follows an in-app navigation, as opposed to a cold load
  // (deep link, refresh). The confirm guard needs the difference: a cold arrival
  // on /confirm may only re-show a receipt, never submit the order again.
  const navigatedRef = useRef(false);

  // Every step opens at its top. The window scroll survives an SPA navigation,
  // so without this the menu opened wherever the previous screen was scrolled —
  // and the browser's own restoration is switched off so that Back agrees.
  useEffect(() => {
    window.history.scrollRestoration = "manual";
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen]);

  // The step, by the name the flow calls it — not the URL the SDK would capture
  // on its own. `/r/butter/q5` is a page; "the free-text question" is a funnel
  // rung, and only one of the two survives a rename of the route.
  useEffect(() => {
    track(EVENTS.STEP_VIEWED, { step: screen });
  }, [screen]);

  const base = `/r/${slug}`;
  const pathFor = (s) => (s === "guest" ? base : `${base}/${s}`);
  const go = (s, opts) => {
    navigatedRef.current = true;
    navigate(pathFor(s), opts);
  };
  // The in-app back arrow means the device's Back wherever there is history to
  // pop; on a deep link there is none, so it replaces to where the arrow points
  // today rather than dropping the guest out of the site.
  const back = (fallback) => {
    navigatedRef.current = true;
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(pathFor(fallback), { replace: true });
  };

  /**
   * Into the plate builder, from wherever it was offered.
   *
   * The tap is the only step of this funnel that leaves no trace of its own —
   * plates shown, plate taken and order placed are all facts we already store —
   * so a guest who gave up in the quiz was indistinguishable from one who never
   * looked. Fire and forget: nobody waits on our bookkeeping to answer a question
   * about lunch.
   */
  const openAuto = () => {
    fetchJson("/events", {
      method: "POST",
      body: JSON.stringify({ event: "ai_opened", restaurant_slug: slug }),
    }).catch(() => {});
    // Beside the beacon, not instead of it: the beacon is the restaurant's own
    // number on its AI dashboard, and this is ours, with the screen the guest
    // was on when the offer worked.
    track(EVENTS.AI_OPENED, { from: screen });
    // A fresh ask starts a fresh quiz. Without this, the answers kept for
    // "Back from the free-text step resumes at question four" would open a
    // new ask on question four too.
    setQuizAnswers(null);
    setFreeText("");
    go("quiz");
  };

  // After sign-in: attach the anonymous pending order (if any) to the new user.
  // The claim token proves this browser is the one that placed it.
  useEffect(() => {
    if (!user) return;
    const key = pendingOrderKey(slug);
    let pending = null;
    try {
      pending = JSON.parse(localStorage.getItem(key) || "null");
    } catch (e) {
      pending = null;
    }
    // Without the claim token the server cannot tell this is our order, and will
    // refuse it — nothing to send.
    if (!pending?.orderId || !pending?.claimToken) {
      if (pending) localStorage.removeItem(key);
      return;
    }
    (async () => {
      try {
        await fetchJson(`/orders/${pending.orderId}/attach`, {
          method: "POST",
          body: JSON.stringify({ claim_token: pending.claimToken }),
        });
        // The one outcome here that happens entirely off-screen: without a
        // word, the guest has no way to know their order followed them in.
        toast(t("toast.orderSaved"));
      } catch (e) {
        // silent — the guest still sees their order, it just is not claimed
      } finally {
        localStorage.removeItem(key);
        refreshUser();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refreshUser, slug]);

  const openReorder = (order) => {
    // Only what the menu still has — a dish taken off since is not reorderable.
    // Everything else travels as it was placed, including which lines the AI
    // composed and the answer they came from: a repeat of an AI plate is the AI
    // working, and re-adding it as a manual line said the opposite.
    const items = (order.items || []).filter((it) => findDish(it.dish_id));
    addReorderToCart({ ...order, items });
    track(EVENTS.ORDER_REORDERED, {
      items: items.length,
      // What the guest asked for versus what the menu still has: a repeat that
      // silently lost half its dishes is a different experience from a clean one.
      dropped: (order.items || []).length - items.length,
    });
    go("cart");
  };

  // This restaurant takes orders only from a device that scanned recently. The
  // gate sits in front of every screen rather than at checkout: a guest who
  // opened yesterday's link should be told before they build a plate, not after.
  // `scanning` holds it open while a fresh `?tt=` is still being exchanged, so a
  // real scan never flashes this screen on its way in.
  const needsScan =
    Boolean(restaurant?.qr?.enforce) && !scanning && !hasLiveTableSession(state.guest);

  // A dead end worth counting: everyone who meets it wanted to order and could
  // not. Declared here rather than inside the branch below — a hook may not hide
  // behind an early return.
  useEffect(() => {
    if (needsScan) track(EVENTS.SCAN_REQUIRED_SHOWN, { reason: qrError || "no_session" });
  }, [needsScan, qrError]);

  if (needsScan) {
    return (
      <div className="App" data-testid="app-root">
        <div className="app-frame" data-testid="app-frame">
          <ScanRequired reason={qrError} />
        </div>
      </div>
    );
  }

  // A URL is an offer, not a fact: a deep link or refresh can name a step whose
  // prerequisites live only in this component's state. Bounce those to the step
  // that can rebuild them instead of rendering a screen that would crash or —
  // in confirm's case — re-submit an order the kitchen already has.
  let bounce = null;
  if (!STEPS.has(screen)) {
    bounce = base;
  } else if ((screen === "q5" || screen === "ai") && !quizAnswers) {
    bounce = pathFor("quiz");
  } else if (
    screen === "confirm" &&
    !(state.lastOrder && (state.lastOrder.server || navigatedRef.current))
  ) {
    bounce = base;
  } else if (screen === "paid" && !state.lastOrder) {
    bounce = base;
  }
  if (bounce) {
    return <Navigate to={bounce} replace />;
  }

  return (
    <div className="App" data-testid="app-root">
      <div className="app-frame" data-testid="app-frame">
        {screen === "guest" && (
          <GuestEntry
            onContinue={() => go("home")}
            qrError={qrError}
            onDismissQrError={dismissQrError}
          />
        )}

        {screen === "home" && (
          <ModeSelect
            onBuild={() => go("build")}
            onAuto={openAuto}
            onDrinks={() => go("drinks")}
            onMenu={() => go("menu")}
            onOpenProfile={() => go("profile")}
            onChangeTable={() => go("guest")}
          />
        )}

        {screen === "build" && (
          <BuildPlate
            onBack={() => back("home")}
            onViewCart={() => go("cart")}
            onAuto={openAuto}
          />
        )}

        {screen === "drinks" && (
          <Drinks onBack={() => back("home")} onViewCart={() => go("cart")} />
        )}

        {screen === "menu" && (
          <Menu
            onBack={() => back("home")}
            onViewCart={() => go("cart")}
            onAuto={openAuto}
          />
        )}

        {screen === "cart" && (
          <Cart
            // Back is history: the guest returns to the screen they were
            // picking dishes on, not to the path chooser. "Add more" stays an
            // explicit door to the menu — that is what its label promises.
            onBack={() => back("home")}
            onAddMore={() => go("home")}
            onAuto={openAuto}
            onPlace={(summary) => {
              placeOrder(summary);
              // Replace: Back from the receipt must not show a now-empty cart.
              go("confirm", { replace: true });
            }}
          />
        )}

        {screen === "quiz" && (
          <Quiz
            onBack={() => back("home")}
            lastAnswers={lastQuiz}
            initialAnswers={quizAnswers || undefined}
            initialStep={quizAnswers ? 3 : 0}
            onComplete={(ans) => {
              setQuizAnswers(ans);
              // freeText survives a walk back through the questions — a fresh
              // ask clears it in openAuto, where fresh actually begins.
              // Remembered on the device, so the next visit can offer them back.
              // Signing in is not the price of not repeating yourself: most
              // guests never do, and they are the ones sitting at the table.
              try {
                localStorage.setItem(lastQuizKey(slug), JSON.stringify(ans));
              } catch (e) {
                // a browser refusing storage just gets the full quiz next time
              }
              setLastQuiz(ans);
              go("q5");
            }}
          />
        )}

        {screen === "q5" && (
          <Q5Input
            onBack={() => back("quiz")}
            initialText={freeText}
            onSubmit={(text) => {
              setFreeText(text);
              go("ai");
            }}
          />
        )}

        {screen === "ai" && (
          <AiPlateResult
            answers={quizAnswers}
            freeText={freeText}
            onBack={() => back("q5")}
            // The error screen's escape hatch goes where its label promises —
            // the builder — and replaces /ai in history for the same reason
            // accepting a plate does: Back must not re-fire a paid model call.
            onBuild={() => go("build", { replace: true })}
            onAccept={(plate, requestId) => {
              addPlateToCart(plate.items, requestId);
              // Replace: /ai must not stay reachable via Back — remounting it
              // fires a fresh paid model call. Back from the cart lands on q5,
              // where rebuilding is a deliberate step.
              go("cart", { replace: true });
            }}
          />
        )}

        {screen === "confirm" && (
          <Confirmation
            onPay={() => go("paid", { replace: true })}
            onNewOrder={() => {
              resetAll();
              setQuizAnswers(null);
              setFreeText("");
              go("guest", { replace: true });
            }}
          />
        )}

        {screen === "paid" && (
          <PaymentSuccess
            onOpenProfile={() => go("profile")}
            onDone={() => {
              resetAll();
              setQuizAnswers(null);
              setFreeText("");
              go("guest", { replace: true });
            }}
          />
        )}

        {screen === "profile" && (
          <Profile
            onBack={() => back(user ? "home" : "guest")}
            onReorder={openReorder}
          />
        )}

        {/* One footer for every screen: the platform's mark on the left, the
            language on the right. The language lives here, not on the first
            screen's header — a guest who realises mid-menu they are reading
            the wrong language should not have to walk back to Welcome.
            `mt-auto` seats it at the frame's foot when the content is short;
            the deep bottom padding exists only where a fixed cart bar floats
            over the page's end. The profile is the one screen without this
            row: its Fuelly moment is the benefits card, and its language
            switcher lives in Settings, where a signed-in guest expects it. */}
        {screen !== "profile" && (
          <div
            className={`mt-auto px-5 pt-6 flex items-center justify-between gap-3 ${
              ["build", "drinks", "menu", "cart"].includes(screen) ? "pb-36" : "pb-8"
            }`}
          >
            <FuellyBadge />
            <LanguageMenu testId={LANGUAGE.footer} />
          </div>
        )}
      </div>
    </div>
  );
};

/** One restaurant's storefront: tenant data, then its cart, then the screens. */
const TenantApp = () => {
  const { slug } = useParams();
  return (
    <TenantProvider>
      {/* key: switching restaurants must not carry a cart across tenants */}
      <OrderProvider key={slug} slug={slug}>
        <Shell />
      </OrderProvider>
    </TenantProvider>
  );
};

/** Anything that is not a restaurant URL — most likely a mistyped/stale link. */
const RouteNotFound = () => {
  const { t } = useTranslation();
  return (
    <div className="App">
      <div className="app-frame">
        <div
          data-testid={ROUTE.notFound}
          className="min-h-screen px-8 flex flex-col items-center justify-center text-center"
        >
          <div className="w-14 h-14 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
            <QrCode className="text-neutral-500" />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-neutral-900">
            {t("route.notFoundTitle")}
          </h1>
          <p className="text-sm text-neutral-500 mt-2 max-w-[32ch]">{t("route.notFoundBody")}</p>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <AuthSheetProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            {/* Fuelly's own documents, not a restaurant's — so they sit beside
                Landing rather than inside TenantApp, and open without a slug.
                They are also what lets the Google consent screen be published:
                it will not go live without a privacy policy at a stable URL. */}
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/terms" element={<Legal doc="terms" />} />
            <Route path="/r/:slug/*" element={<TenantApp />} />
            <Route path="*" element={<RouteNotFound />} />
          </Routes>
          </BrowserRouter>
        </AuthSheetProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
